/**
 * Client-side image compression for patient ID documents.
 *
 * Goals:
 * - Keep legibility for OCR and human review (text on RG/CNH must stay readable)
 * - Reduce storage/network cost (Firebase Storage billing + mobile uploads)
 *
 * Strategy:
 * - Resize longest dimension down to `maxDim` (default 1600px — readable RG at A4-ish)
 * - Re-encode as JPEG at `quality` (default 0.85 — subjectively lossless for text)
 * - Preserve orientation by drawing through canvas (EXIF orientation is flattened)
 *
 * Typical output: a 12MP phone photo (~4MB) shrinks to ~300–600KB while keeping
 * OCR accuracy. No external dependency needed.
 */

export interface CompressOptions {
  maxDim?: number;
  quality?: number;
  /** Force output mime. Defaults to the input type when it's jpeg/png/webp, else jpeg. */
  mimeType?: 'image/jpeg' | 'image/png' | 'image/webp';
}

const DEFAULT_MAX_DIM = 1600;
const DEFAULT_QUALITY = 0.85;

export async function compressImage(file: File, options: CompressOptions = {}): Promise<File> {
  const maxDim = options.maxDim ?? DEFAULT_MAX_DIM;
  const quality = options.quality ?? DEFAULT_QUALITY;

  // Pick output mime — JPEG is the safe default for photos/scans.
  const outMime: string =
    options.mimeType
    ?? (file.type === 'image/png' ? 'image/png'
      : file.type === 'image/webp' ? 'image/webp'
      : 'image/jpeg');

  // Skip if file is already small and in a sensible format
  if (file.size < 200 * 1024 && file.type.startsWith('image/')) {
    return file;
  }

  const bitmap = await loadBitmap(file);
  const { width: srcW, height: srcH } = bitmap;

  // Compute target size while preserving aspect ratio
  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const destW = Math.round(srcW * scale);
  const destH = Math.round(srcH * scale);

  const canvas = document.createElement('canvas');
  canvas.width = destW;
  canvas.height = destH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    // Very old browser — fall back to original
    return file;
  }

  // Subtle smoothing; "high" keeps text crisper than default
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, 0, 0, destW, destH);

  const blob: Blob | null = await new Promise((resolve) => {
    canvas.toBlob(
      (b) => resolve(b),
      outMime,
      outMime === 'image/png' ? undefined : quality,
    );
  });

  if (!blob) return file;

  // Keep original filename but swap extension if the mime changed
  const base = file.name.replace(/\.[^.]+$/, '');
  const ext = outMime === 'image/png' ? '.png' : outMime === 'image/webp' ? '.webp' : '.jpg';
  const outName = base + ext;

  return new File([blob], outName, { type: outMime, lastModified: Date.now() });
}

/**
 * Load a File into an ImageBitmap (fast path) or HTMLImageElement fallback.
 * ImageBitmap is supported by Chrome/Firefox/Edge and honours EXIF orientation
 * when `imageOrientation: 'from-image'` is passed.
 */
async function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    } catch {
      // Fall through
    }
  }
  return loadImageElement(file);
}

function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Human-readable size formatter for UI feedback.
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}
